import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, Briefcase, ExternalLink, MapPin, Car } from 'lucide-react'
import { BottomNavigator } from '@/components/BottomNavigator'
import { MultiCityFilter } from '@/components/MultiCityFilter'
import { spacing } from '@/theme'
import { useResponsive } from '@/hooks/useResponsive'
import { api } from '@/api/axios'
import { useEffect, useState } from 'react'

export const VEHICLE_TYPES = ['Automóvil', 'Bus', 'Buseta', 'Camioneta', 'Camión', 'Tractomula', 'Taxi', 'Moto', 'Otro']

export const toArray = (v: string | string[] | null | undefined): string[] => {
  if (!v) return []
  if (Array.isArray(v)) return v
  try {
    const parsed = JSON.parse(v)
    if (Array.isArray(parsed)) return parsed.map(String)
  } catch {
    // legacy single string
  }
  return [v]
}

type Advertisement = {
  id: string
  title: string
  image: string | null
  link: string | null
  description: string
  startDate: string
  endDate: string
  city: string | string[] | null
  vehicleType: string | string[] | null
}

export function OfertasLaboralesScreen() {
  const navigate = useNavigate()
  const { isDesktop } = useResponsive()
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([])
  const [loading, setLoading] = useState(true)
  const [cityFilter, setCityFilter] = useState<string[]>([])
  const [vehicleFilter, setVehicleFilter] = useState<string[]>([])

  useEffect(() => {
    api.get('/advertisements/active').then((res) => {
      setAdvertisements(res.data.data || [])
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const filtered = advertisements.filter((a) => {
    const cities = toArray(a.city)
    const vehicles = toArray(a.vehicleType)
    if (cityFilter.length > 0 && !cityFilter.some((c) => cities.includes(c))) return false
    if (vehicleFilter.length > 0 && !vehicleFilter.some((v) => vehicles.includes(v))) return false
    return true
  })
  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          padding: spacing[4],
          paddingBottom: '56px',
          background: 'linear-gradient(180deg, #020617 0%, #0f172a 30%, #1d4ed8 70%, #0f172a 100%)',
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <button
            onClick={() => navigate('/interacciones')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing[2],
              color: '#93c5fd',
              marginBottom: spacing[3],
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <ArrowLeft size={18} />
            Volver
          </button>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              borderRadius: 24,
              padding: spacing[5],
              marginBottom: spacing[4],
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(30, 64, 175, 0.4) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 160,
              height: 160,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%)',
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3], position: 'relative', zIndex: 1 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
              }}>
                <Briefcase size={24} color="#fff" />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#dbeafe' }}>Ofertas Laborales</h1>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#93c5fd' }}>Oportunidades de trabajo para ti</p>
              </div>
            </div>
          </motion.div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3], marginBottom: spacing[4] }}>
            <MultiCityFilter value={cityFilter} onChange={setCityFilter} label="Filtrar por ciudad" />
            <div>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>Filtrar por tipo de vehículo</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {VEHICLE_TYPES.map((vt) => {
                  const active = vehicleFilter.includes(vt);
                  return (
                    <button
                      key={vt}
                      onClick={() => setVehicleFilter(active ? vehicleFilter.filter((v) => v !== vt) : [...vehicleFilter, vt])}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '7px 12px',
                        borderRadius: 999,
                        border: active ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(148, 163, 184, 0.2)',
                        background: active ? 'rgba(59, 130, 246, 0.15)' : 'rgba(15, 23, 42, 0.6)',
                        color: active ? '#93c5fd' : '#94a3b8',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <Car size={13} />
                      {vt}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ borderRadius: 20, overflow: 'hidden', background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(59, 130, 246, 0.1)' }}>
                  <div style={{ height: 160, background: 'rgba(59, 130, 246, 0.05)' }} />
                  <div style={{ padding: spacing[3] }}>
                    <div style={{ width: '70%', height: 16, borderRadius: 8, background: 'rgba(59, 130, 246, 0.1)', marginBottom: 8 }} />
                    <div style={{ width: '100%', height: 10, borderRadius: 8, background: 'rgba(59, 130, 246, 0.05)', marginBottom: 4 }} />
                    <div style={{ width: '80%', height: 10, borderRadius: 8, background: 'rgba(59, 130, 246, 0.05)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: spacing[10], color: '#64748b' }}>
              <Briefcase size={48} style={{ margin: '0 auto', opacity: 0.3 }} />
              <p style={{ marginTop: spacing[2], fontSize: 15 }}>{cityFilter.length > 0 || vehicleFilter.length > 0 ? 'No hay ofertas con esos filtros' : 'No hay ofertas laborales activas'}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(2, 1fr)' : '1fr', gap: spacing[3] }}>
              {filtered.map((adv, idx) => (
                <motion.div
                  key={adv.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={adv.link ? { y: -4, scale: 1.01 } : {}}
                  whileTap={adv.link ? { scale: 0.99 } : {}}
                  onClick={() => { if (adv.link) window.open(adv.link, '_blank', 'noopener,noreferrer') }}
                  style={{
                    borderRadius: 20,
                    overflow: 'hidden',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 100%)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: adv.link ? 'pointer' : 'default',
                  }}
                >
                  {adv.image && (
                    <div style={{ position: 'relative', height: 180, overflow: 'hidden' }}>
                      <img
                        src={adv.image}
                        alt={adv.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: 'linear-gradient(to top, rgba(15, 23, 42, 0.9) 0%, transparent 100%)',
                      }} />
                    </div>
                  )}
                  <div style={{ padding: spacing[3], display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0', lineHeight: 1.3 }}>
                      {adv.title}
                    </h3>
                    {toArray(adv.vehicleType).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {toArray(adv.vehicleType).map((vt) => (
                          <span key={vt} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, color: '#dbeafe', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(30, 64, 175, 0.15))', border: '1px solid rgba(59, 130, 246, 0.3)', width: 'fit-content' }}>
                            <Car size={12} /> {vt}
                          </span>
                        ))}
                      </div>
                    )}
                    {toArray(adv.city).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {toArray(adv.city).map((c) => (
                          <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#93c5fd', fontWeight: 600 }}>
                            <MapPin size={13} /> {c}
                          </span>
                        ))}
                      </div>
                    )}
                    <p style={{ margin: 0, fontSize: 13, color: '#94a3b8', lineHeight: 1.5, flex: 1 }}>
                      {adv.description}
                    </p>
                    {adv.link && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>
                        <ExternalLink size={13} /> Ver más
                      </span>
                    )}
                    <div style={{ display: 'flex', gap: spacing[3], fontSize: 11, color: '#64748b', marginTop: spacing[1], paddingTop: spacing[2], borderTop: '1px solid rgba(148, 163, 184, 0.1)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={12} color="#3b82f6" /> {formatDate(adv.startDate)}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={12} color="#3b82f6" /> {formatDate(adv.endDate)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNavigator />
    </>
  )
}
